import {NgxDataSourceTrigger} from '@ngx-component-kit/tables/tables.interfaces';
import {makeStateKey, Meta, TransferState} from '@angular/platform-browser';
import {NgxNotifications} from '@ngx-notifications/notifications.service';
import {SessionService} from '@core/services/session.service';
import {filter, first, map, share} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {environment} from '@env/environment';
import {Constants} from '@app/app.constants';
import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class PlatformsService {

    private readonly _platformStateKey = makeStateKey('platform');

    // The current platform.
    private _currentPlatform$ = new BehaviorSubject<{ platform: Api.Platform | null } | null>(null);

    constructor(private _meta: Meta,
                private _http: HttpClient,
                private _state: TransferState,
                private _session: SessionService,
                private _notifications: NgxNotifications) {
        // Set the current platform.
        this._currentPlatform$.next(
            this._state.get(this._platformStateKey, null)
        );
    }

    get platformId() {
        // If the environment is not set to production return
        // the mock platform id.
        if (environment.mockPlatformId) {
            return environment.mockPlatformId;
        }

        // Get the meta tag from the document.
        const tag = this._meta.getTag('name=video-platform-id');

        // Check if the tag exists, if so return the content.
        return tag ? tag.content : null;
    }

    get current() {
        // Return the current platform as a shared observable.
        return this._currentPlatform$.pipe(
            map(data => data && data.platform ? data.platform : null)
        );
    }

    all(trigger: NgxDataSourceTrigger) {
        const url = this._session.isAgent
            ? `${Constants.endpoints.platforms}/agent`
            : Constants.endpoints.platforms;
        return this._http.get<Api.DataSource<Api.Platform>>(url, {
            params: {
                page: trigger.pager.pageNumber + '',
                page_size: trigger.pager.pageSize + '',
                sort: 'title',
                sort_direction: 'asc',
                query: trigger.query,
                query_fields: trigger.queryFields
            }
        }).pipe(
            share()
        );
    }

    get(platformId: string) {
        const url = this._session.isAdmin
            ? `${Constants.endpoints.platforms}/${platformId}/admin`
            : `${Constants.endpoints.platforms}/${platformId}`;

        return this._http.get<Api.Platform>(url).pipe(
            share()
        );
    }

    async create(platform: Api.Platform) {
        try {
            // Create a new platform in the database.
            return await this._http.post<Api.Platform>(Constants.endpoints.platforms, {platform: platform})
                .toPromise();
        } catch (error) {
            if (error.error.errors.subdomain) {
                this._notifications.error('The subdomain is already taken.');
            } else {
                this._notifications.error('Something went wrong.');
            }
            throw error;
        }
    }

    async update(id: string, platform: Partial<Api.Platform>, withNotification: boolean = true) {
        try {
            // Update a platform in the database.
            const result = await this._http.put<Api.Platform>(`${Constants.endpoints.platforms}/${id}`, {platform})
                .toPromise();

            // Update the current platform if the user is a platform admin.
            if (this._session.isPlatformAdmin) {
                this._currentPlatform$.next({platform: result});
            }

            // Notify the user.
            if (withNotification) {
                this._notifications.success('The platform has been successfully updated.');
            }

            return result;
        } catch (error) {
            console.log(error);
        }
    }

    async delete(id: string) {
        try {
            // Delete a platform in the database.
            const result = await this._http.delete<void>(`${Constants.endpoints.platforms}/${id}`)
                .toPromise();

            // Notify the user.
            this._notifications.success('The platform has been successfully deleted.');

            return result;
        } catch (error) {
            console.log(error);
        }
    }

    async setLogo(platformId: string, file: File) {
        // Create a form data with the file.
        const formData = new FormData();
        formData.append('logo', file, file.name);

        try {
            // Wait until the logo is uploaded.
            const url = `${Constants.endpoints.platforms}/${platformId}/logo`;

            return await this._http.put<Api.Platform>(url, formData).toPromise();
        } catch (error) {
            console.log(error);
        }
    }

    initialize() {
        return async () => {
            return new Promise(async resolve => {
                // Check if the platform id exists.
                if (!this.platformId) {
                    return;
                }

                try {
                    // Await until the platform is retrieved.
                    const platform = await this.get(this.platformId).toPromise();

                    // Set the state.
                    this._state.set(this._platformStateKey, {platform});

                    // Push the platform.
                    this._currentPlatform$.next({platform});
                } catch (error) {
                    // Set the state.
                    this._state.set(this._platformStateKey, null);

                    // We push an empty platform so that the observable emits because of a value.
                    this._currentPlatform$.next({platform: null});
                }

                resolve();
            });
        };
    }

    async getPlatform() {
        if (!this.platformId) {
            return null;
        }

        try {
            // The filter method makes sure that the call will not complete
            // until the platform has been loaded. This is required for
            // SEO to work properly.
            return await this._currentPlatform$.pipe(
                filter(data => !!data),
                map(data => data.platform || null),
                first()
            ).toPromise();
        } catch (error) {
            return null;
        }
    }

}
